import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styles from "./FeedPage.module.css";
import PageHeader from "../../Shared/PageHeader";
import SearchBar from "../../Shared/SearchBar";
import Button from "../../Shared/Button";
import FilterCheckList from "../../Shared/FilterCheckList";
import FeedList from "../../Shared/FeedList";
import CloseDialog from "../../Shared/CloseDialog";
import ConfirmationDialog from "../../Shared/ConfirmationDialog";
import { useFeedPage } from "./useFeedPage";
import { mapApiResponseToFeedItem } from "../../utils/mapFeedItems";
import Loader from "shared/Loader";
import {
  useFeedItemOptionsDropdown,
  useFeedItemOptionsDropdownGroup,
} from "NewsAdmin/Shared/FeedItemOptionsDropdown/useFeedItemOptionsDropdown";

function EmptyFeedMessage() {
  return (
    <div style={{ padding: "2rem", textAlign: "center", color: "#888" }}>
      No feed items found. Try adjusting your filters.
    </div>
  );
}

export function FeedPage() {
  const history = useHistory();
  const location = useLocation();
  const {
    data: feedData,
    isLoading,
    isFetching,
    isSaving,
    refetch,
    cloneFeedItemAU,
    syncFeedToAU,
  } = useFeedPage();
  const { data: diamondsList, isLoading: isLoadingDiamonds } =
    useFeedItemOptionsDropdown();

  const { data: groupsList, isLoading: isLoadingGroups } =
    useFeedItemOptionsDropdownGroup();

  const [searchText, setSearchText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isAUOpen, setIsAUOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [checkedOptions, setCheckedOptions] = useState([
    "Unpublished",
    "Published",
  ]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCloseOpen, setIsCloseOpen] = useState(false);
  const [clonedItemId, setClonedItemId] = useState(null);
  const [clonedItemType, setClonedItemType] = useState(null);
  const shouldShowRouteLoaderOnEntry = Boolean(location.state?.showFeedLoader);
  const expectedFeedId = location.state?.expectedFeedId;
  const expectedFeedTitle = location.state?.expectedFeedTitle;
  const routeLoaderStartedAtRef = useRef(
    shouldShowRouteLoaderOnEntry ? Date.now() : 0
  );
  const consistencyRefetchAttemptsRef = useRef(0);
  const [isRouteTransitionLoading, setIsRouteTransitionLoading] = useState(
    shouldShowRouteLoaderOnEntry
  );

  // Clone
  const onClone = (itemID, type) => {
    history.push(`/marketing/news/feed/${itemID}`, {
      pageType: type,
      isCloning: true,
    });
  };

  // CloneAU
  const onCloneAU = (feedItem) => {
    cloneFeedItemAU(
      { feedItem, diamondsList, groupsList },
      {
        onSuccess: () => {
          setIsAUOpen(true);
          // history.push("/marketing/news/feed");
        },
        onError: (err) => {
          console.error("Create failed:", err);
        },
      }
    );
  };

  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [isSyncConOpen, setIsSyncConOpen] = useState(false);
  const [syncingFeedItem, setSyncingFeedItem] = useState(null);
  // Sync
  const onSync = (feedItem) => {
    setSyncingFeedItem(feedItem);
    setShowSyncDialog(true);
  };
  const onSyncConfirmation = () => {
    setShowSyncDialog(false);
    syncFeedToAU(
      { feedItem: syncingFeedItem, diamondsList, groupsList },
      {
        onSuccess: () => {
          setIsSyncConOpen(true);
        },
        onError: (err) => {
          console.error("Create failed:", err);
        },
      }
    );
  };

  const handleConfirmDialog = () => {
    setIsDialogOpen(false);
    setIsCloseOpen(true);
    // alert("confirm");
  };

  const handleCloseDialog = () => {
    setIsCloseOpen(false);
    setIsDialogOpen(false);
    history.push(`/marketing/news/feed/${clonedItemId}`, { clonedItemType });
    // alert("close");
  };

  const sortedFeedItems = useMemo(() => {
    if (!feedData || !diamondsList || !groupsList) {
      return [];
    }

    const rawFeedItems = feedData.map((item) =>
      mapApiResponseToFeedItem(item, diamondsList, groupsList)
    );

    const normalize = (val) =>
      (val == null ? "" : String(val)).toLowerCase().trim();

    const statusMap = {
      unpublished: 0,
      published: 1,
      archived: 2,
      storage: 3,
    };

    const active = { feedType: [], section: [], status: [] };

    checkedOptions.forEach((option) => {
      const opt = normalize(option);
      if (opt === "standard content" || opt === "standing order") {
        active.feedType.push(opt);
        return;
      }
      if (opt === "cards") {
        active.section.push("card");
        return;
      }
      if (opt === "banners") {
        active.section.push("banner");
        return;
      }
      if (opt in statusMap) {
        active.status.push(statusMap[opt]);
      }
    });

    const checkedData = checkedOptions.length
      ? rawFeedItems.filter((item) => {
          const group = Number(item?.options?.group);
          const groupType = group === 1 ? "card" : group === 2 ? "banner" : null;

          const itemType = normalize(item?.type);

          const sectionMatch =
            active.section.length === 0 ||
            (groupType && active.section.includes(groupType));

          const feedTypeMatch = (() => {
            if (active.feedType.length === 0) return true;
            if (groupType === "banner") return false;
            return active.feedType.includes(itemType);
          })();

          const statusMatch =
            active.status.length > 0 &&
            active.status.includes(item?.options?.status);

          return sectionMatch && feedTypeMatch && statusMatch;
        })
      : [];

    const filteredData =
      searchText !== ""
        ? checkedData.filter((item) =>
            (item?.options?.title ?? "")
              .toLowerCase()
              .includes(searchText.toLowerCase())
          )
        : checkedData;

    const getDate = (item) =>
      item?.options?.publishDate?.date || item?.options?.availableOn || null;

    if (normalize(selectedOption) === "sort by date: ascending") {
      return [...filteredData].sort(
        (a, b) => new Date(getDate(a)) - new Date(getDate(b))
      );
    }

    return [...filteredData].sort(
      (a, b) => new Date(getDate(b)) - new Date(getDate(a))
    );
  }, [feedData, diamondsList, groupsList, checkedOptions, searchText, selectedOption]);

  useEffect(() => {
    if (!shouldShowRouteLoaderOnEntry) {
      return;
    }
    history.replace(location.pathname, {
      ...(location.state || {}),
      showFeedLoader: false,
    });
  }, [history, location.pathname, location.state, shouldShowRouteLoaderOnEntry]);

  useEffect(() => {
    if (!isRouteTransitionLoading) {
      return;
    }

    const isDataReady =
      !isLoading && !isFetching && !isSaving && !isLoadingDiamonds && !isLoadingGroups;

    if (!isDataReady) {
      return;
    }

    const shouldValidateExpectedTitle =
      expectedFeedId != null && expectedFeedTitle != null;

    if (shouldValidateExpectedTitle) {
      const matchedFeed = sortedFeedItems.find(
        (item) => String(item?.options?.id) === String(expectedFeedId)
      );
      const titleMatches =
        (matchedFeed?.options?.title ?? "").trim() ===
        String(expectedFeedTitle).trim();

      if (!titleMatches && consistencyRefetchAttemptsRef.current < 2) {
        consistencyRefetchAttemptsRef.current += 1;
        refetch();
        return;
      }
    }

    const elapsed = Date.now() - routeLoaderStartedAtRef.current;
    const minimumLoaderMs = 350;
    const remaining = Math.max(0, minimumLoaderMs - elapsed);
    const timerId = setTimeout(() => {
      setIsRouteTransitionLoading(false);
    }, remaining);

    return () => clearTimeout(timerId);
  }, [
    isRouteTransitionLoading,
    isLoading,
    isFetching,
    isSaving,
    isLoadingDiamonds,
    isLoadingGroups,
    sortedFeedItems,
    expectedFeedId,
    expectedFeedTitle,
    refetch,
  ]);

  const isFeedListLoading =
    isLoading ||
    isFetching ||
    isSaving ||
    isLoadingDiamonds ||
    isLoadingGroups ||
    isRouteTransitionLoading;

  const onAddNew = () => {
    history.push(`/marketing/news/feed/new`);
  };
  const onEdit = (itemID, pageType) => {
    history.push(`/marketing/news/feed/${itemID}`, { pageType });
  };

  return (
    <>
      <CloseDialog
        selectedOption={selectedOption}
        setSelectedOption={(value) =>
          setSelectedOption((prev) => (prev === value ? "" : value))
        }
        buttonClick={() => {
          setIsOpen(false);
        }}
        isOpen={isOpen}
        sortOptions={["Sort by Date: Descending", "Sort by Date: Ascending"]}
        buttonText={"Close"}
        headerText={"Sort By"}
      />

      <div className={styles["close-dialog"]}>
        <CloseDialog
          headerText={"Feed Item was cloned"}
          buttonText={"Okay"}
          selectedOption={""}
          setSelectedOption={() => {}}
          buttonClick={() => setIsAUOpen(false)}
          isOpen={isAUOpen}
        />
        <ConfirmationDialog
          confirmationText={`You're about to SYNC this feed item to AU Production with ID:${syncingFeedItem?.options?.syncFeedId}. Continue?`}
          onConfirmation={() => onSyncConfirmation()}
          isVisible={showSyncDialog}
          onDismiss={() => setShowSyncDialog(false)}
        />
        <CloseDialog
          headerText={`Feed item ID:${syncingFeedItem?.options?.id} was synced to AU Production with ID:${syncingFeedItem?.options?.syncFeedId}.`}
          buttonText={"Okay"}
          selectedOption={""}
          setSelectedOption={() => {}}
          buttonClick={() => setIsSyncConOpen(false)}
          isOpen={isSyncConOpen}
        />
      </div>

      <ConfirmationDialog
        isVisible={isDialogOpen}
        onConfirmation={handleConfirmDialog}
        onDismiss={() => {
          setIsDialogOpen(false);
        }}
        confirmationText={
          "You are about to CLONE this feed item to AU Production as Unpublished. Continue?"
        }
      />

      <div className={styles["close-dialog"]}>
        <CloseDialog
          headerText={"Feed Item was cloned."}
          buttonText={"Okay"}
          selectedOption={""}
          // setSelectedOption={""}
          setSelectedOption={() => {}} // Pass no-op function to prevent errors, rather than a string
          buttonClick={handleCloseDialog}
          isOpen={isCloseOpen}
        />
      </div>

      <div className={styles.home}>
        <div className={styles.subContainer}>
          <PageHeader isHome={true} />
          <div className={styles.flex}>
            <SearchBar
              placeholderText={"Search Feed"}
              onInput={(text) => setSearchText(text)}
              searchDelay={0}
              minChar={0}
            />
            <Button
              className={styles.btnNew}
              onClick={onAddNew}
              text={"Add New Feed"}
            />
            <Button
              className={styles.btn}
              onClick={() => setIsOpen(true)}
              text={"Sort"}
            />
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.filter}>
            <FilterCheckList
              filterOptions={[
                {
                  categoryTitle: "Feed Type",
                  categoryOptions: ["Standard Content", "Standing Order"],
                },
                {
                  categoryTitle: "Section",
                  categoryOptions: ["Banners", "Cards"],
                },
                {
                  categoryTitle: "Status",
                  categoryOptions: [
                    "Unpublished",
                    "Published",
                    "Archived",
                    "Storage",
                  ],
                },
              ]}
              checkedOptions={checkedOptions}
              setCheckedOptions={(option) => {
                if (checkedOptions.includes(option)) {
                  setCheckedOptions(
                    checkedOptions.filter((item) => item !== option)
                  );
                } else {
                  setCheckedOptions([...checkedOptions, option]);
                }
              }}
            />
          </div>

          <div className={styles.feedList}>
            {isFeedListLoading ? (
              <Loader />
            ) : sortedFeedItems.length === 0 ? (
              <EmptyFeedMessage />
            ) : (
              <FeedList
                feedItems={sortedFeedItems}
                onClone={onClone}
                onCloneAU={onCloneAU}
                onEdit={onEdit}
                onSync={onSync}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
